/* @jsx h */
import { defineComponent, h } from "vue";
import { formatPriceInLocale } from "@lemmonhq/orders";

export type CategoryTreeColumnFormat = "money" | "integer" | "quantity";

export type CategoryTreeColumn = {
  key: string;
  label: string;
  format: CategoryTreeColumnFormat;
  /**
   * Whether the category subtotal row shows this column. Defaults to true;
   * turn it off for non-additive values (e.g. a unit price) whose sum means
   * nothing.
   */
  total?: boolean;
};

export type CategoryTreeProduct = {
  id: number | string;
  name: string;
  /** A null value renders as "—" (e.g. a price the product no longer has). */
  values: Record<string, number | null>;
};

export type CategoryTreeGroup = {
  id: number | string;
  name: string;
  color?: string | null;
  values: Record<string, number>;
  products: CategoryTreeProduct[];
};

export type CategoryProductTreeProps = {
  categories: CategoryTreeGroup[];
  columns: CategoryTreeColumn[];
  currency: string;
  collapsed: Set<number | string>;
  onToggleCategory: (id: number | string) => void;
  loading?: boolean;
  error?: boolean;
  emptyMessage: string;
  errorMessage?: string;
  retryLabel?: string;
  onRetry?: () => void;
  productColumnLabel: string;
  categoryTotalLabel: string;
  expandLabel: (name: string) => string;
  collapseLabel: (name: string) => string;
};

/**
 * Presentational category → product tree table. No data fetching of its own
 * — the caller supplies already-grouped `categories` (each with rolled-up
 * `values` and a flat `products` list) so this same markup can back both
 * Chart of Accounts' category tab and the Data Room category report, which
 * pull their rows from different endpoints and shapes.
 */
export const CategoryProductTree = defineComponent<CategoryProductTreeProps>({
  name: "CategoryProductTree",
  props: [
    "categories",
    "columns",
    "currency",
    "collapsed",
    "onToggleCategory",
    "loading",
    "error",
    "emptyMessage",
    "errorMessage",
    "retryLabel",
    "onRetry",
    "productColumnLabel",
    "categoryTotalLabel",
    "expandLabel",
    "collapseLabel",
  ],
  setup(props) {
    // The emphasized (last) column carries the currency code — every other
    // cell stays bare so a dense row of numbers doesn't repeat "CHF" nine times.
    const formatCell = (
      value: unknown,
      format: CategoryTreeColumnFormat,
      includeCurrency = false,
    ) =>
      value === null
        ? "—"
        : format === "money"
          ? formatPriceInLocale(
              Number(value) || 0,
              includeCurrency,
              2,
              props.currency,
            )
          : // Unit counts are shown whole. A quantity can be fractional in
            // the ledger (a split payment recognizes part of an item), but it
            // is rounded for display, like the Data Room's other tables and
            // its Excel/PDF exports.
            new Intl.NumberFormat("de-CH", {
              maximumFractionDigits: 0,
            }).format(Number(value) || 0);

    const amountCell = (
      value: unknown,
      format: CategoryTreeColumnFormat,
      emphasized: boolean,
      surface: string,
    ) => (
      <td
        class={`px-4 py-3 text-right tabular-nums whitespace-nowrap ${
          emphasized ? "font-semibold text-accent" : "text-base-content/70"
        } border-b  group-hover:bg-base-200 ${surface}`}
      >
        {formatCell(value, format, emphasized)}
      </td>
    );

    return () => {
      if (props.loading) {
        return (
          <div class="flex justify-center items-center py-16">
            <span class="loading loading-spinner loading-lg text-primary opacity-60" />
          </div>
        );
      }
      if (props.error) {
        return (
          <div class="flex flex-col items-center justify-center py-16 text-error gap-2">
            <span class="material-symbols-outlined text-4xl opacity-60">
              error_outline
            </span>
            <p class="text-sm">{props.errorMessage}</p>
            {props.onRetry && (
              <button
                class="btn btn-sm btn-outline btn-error"
                onClick={() => props.onRetry!()}
              >
                {props.retryLabel}
              </button>
            )}
          </div>
        );
      }
      if (!props.categories.length) {
        return (
          <div class="py-16 text-center text-sm text-base-content/50">
            {props.emptyMessage}
          </div>
        );
      }

      const numericColumns = props.columns;

      return (
        <div class="overflow-hidden rounded-2xl border  bg-base-100 shadow-sm">
          <div class="overflow-x-auto">
            <table class="table table-sm w-full min-w-[760px] border-separate border-spacing-0">
              <thead>
                <tr class="text-xs uppercase tracking-wide text-base-content/55">
                  <th class="sticky top-0 left-0 z-40 min-w-64 px-4 py-3 bg-base-200 border-r border-b ">
                    {props.productColumnLabel}
                  </th>
                  {numericColumns.map((col) => (
                    <th
                      key={col.key}
                      class="sticky top-0 z-30 min-w-36 px-4 py-3 text-right bg-base-200 border-b "
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {props.categories.map((category) => {
                  const isCollapsed = props.collapsed.has(category.id);
                  return [
                    <tr key={`category-${category.id}`}>
                      <td class="sticky left-0 z-20 min-w-64 px-3 py-2.5 bg-base-200 border-r border-b ">
                        <div class="flex items-center gap-2">
                          <button
                            type="button"
                            class="btn btn-ghost btn-xs btn-square text-accent"
                            aria-expanded={!isCollapsed}
                            aria-label={
                              isCollapsed
                                ? props.expandLabel(category.name)
                                : props.collapseLabel(category.name)
                            }
                            onClick={() => props.onToggleCategory(category.id)}
                          >
                            <span class="material-symbols-outlined text-lg">
                              {isCollapsed ? "chevron_right" : "expand_more"}
                            </span>
                          </button>
                          <span
                            class="h-3 w-3 rounded-full border  shrink-0"
                            style={{
                              background: category.color || "hsl(var(--p))",
                            }}
                          />
                          <span class="font-semibold text-accent">
                            {category.name}
                          </span>
                          <span class="badge badge-primary badge-outline badge-sm">
                            {category.products.length}
                          </span>
                        </div>
                      </td>
                      <td colspan={numericColumns.length} class="" />
                    </tr>,
                    ...(!isCollapsed
                      ? category.products.map((product, productIndex) => {
                          // Opaque, not translucent (`/30` let the row scrolled
                          // behind the sticky first column show through it) —
                          // and applied to every cell, not just the first, so
                          // the zebra stripe reads across the whole row.
                          const surface =
                            productIndex % 2 === 0
                              ? "bg-base-100"
                              : "bg-base-200";
                          return (
                            <tr
                              key={`product-${product.id}`}
                              class="group transition-colors"
                            >
                              <td
                                class={`sticky left-0 z-10 min-w-64 px-4 py-3 font-medium text-accent border-r border-b  group-hover:bg-base-200 ${surface}`}
                              >
                                {product.name}
                              </td>
                              {numericColumns.map((col, i) =>
                                amountCell(
                                  product.values[col.key],
                                  col.format,
                                  i === numericColumns.length - 1,
                                  surface,
                                ),
                              )}
                            </tr>
                          );
                        })
                      : []),
                    ...(!isCollapsed
                      ? [
                          <tr
                            key={`total-${category.id}`}
                            class="font-semibold bg-base-200"
                          >
                            <th class="sticky left-0 z-10 px-4 py-3 text-accent bg-base-200 border-r border-b ">
                              {props.categoryTotalLabel}
                            </th>
                            {numericColumns.map((col, i) => (
                              <th
                                key={col.key}
                                class="px-4 py-3 text-right tabular-nums text-accent border-b "
                              >
                                {col.total === false
                                  ? ""
                                  : formatCell(
                                      category.values[col.key],
                                      col.format,
                                      i === numericColumns.length - 1,
                                    )}
                              </th>
                            ))}
                          </tr>,
                        ]
                      : []),
                  ];
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    };
  },
});

export default CategoryProductTree;
